<template>
  <div class="document-list flex-grow-1">
    <div class="document-list__header d-flex">
      <div class="d-flex flex-column">
        <span class="i-text-heading-4 title">Documents</span>
        <span class="i-text-body-2 i-mt-1"
          >View and manage documents for this customer</span
        >
      </div>
      <div class="d-flex align-items-center">
        <i-button
          v-if="showUploadButton && showCollectButton"
          text="Collect"
          size="lg"
          type="neutral"
          subtype="outline"
          aria-label="Create Collect Session"
          data-testid="create-collect-session-button"
          class="i-mr-4"
          :disabled="isUploadAccessBlocked"
          @button-click="showCollectModal = true"
        />
        <upload-button
          v-if="showUploadButton"
          data-testid="upload-button"
          :customer-id="selectedCustomer.id"
          :disabled="isUploadAccessBlocked"
          @file-added="fileAdded"
          @upload-error="uploadError"
          @upload-file-success="uploadFileSuccess"
          @file-upload-progress="updateFileProgress"
        />
        <create-collect-session-modal
          v-if="!isUploadAccessBlocked"
          :show="showCollectModal"
          :selected-customer="selectedCustomer"
          @hide="showCollectModal = false"
        />
      </div>
    </div>

    <div class="document-list__documents">
      <div v-if="getDocumentsLoading" class="p-5">
        <i-spinner size="xl" />
      </div>
      <div
        v-else
        class="d-flex flex-column flex-grow-1 justify-content-center position-relative"
      >
        <upload
          :auto-process-queue="true"
          :api-url="`api/v2/customers/${$route.params.customer_id}/documents`"
          :border="false"
          :show-empty-state="emptyList"
          :options="{
            addedfile: () => null,
            processing: fileAdded,
          }"
          :include-styling="false"
          :disabled="isUploadAccessBlocked"
          data-testid="document-list-upload"
          @upload-error="uploadError"
          @upload-file-success="uploadFileSuccess"
          @file-upload-progress="updateFileProgress"
        >
          <template slot="drag-overlay"> Drop your files here </template>

          <template slot="empty-state">
            <div
              class="empty-state d-flex flex-column flex-grow-1 justify-content-center align-items-center"
              data-testid="document-list-empty-state"
              :style="{
                cursor: isUploadAccessBlocked ? 'default' : 'pointer',
              }"
            >
              <div
                class="icon d-flex justify-content-center align-items-center"
              >
                <font-awesome-icon class="mr-1" icon="files" />
              </div>
              <h5>No documents added yet</h5>
              <p v-if="!isUploadAccessBlocked">
                <u>Choose a file</u> or drag and drop here
              </p>
            </div>
          </template>
          <template
            v-if="!emptyListUnique"
            slot="default"
            slot-scope="{ isDragging }"
          >
            <div
              :class="[
                'd-flex flex-column w-100 i-mt-8',
                { dragging: isDragging },
              ]"
            >
              <div class="docs-list">
                <document-item
                  v-for="document in $store.state.documents"
                  :key="document.id"
                  :document="document"
                  :options="{ trustScoreSize: 'large' }"
                />
              </div>
            </div>
          </template>
        </upload>

        <div
          v-if="hasPagination"
          class="pagination-wrapper d-flex flex-row align-items-center justify-content-center"
        >
          <!-- normal list -->
          <b-pagination-nav
            id="document-list-pagination"
            class="ui-pagination"
            size="sm"
            align="center"
            :disabled="paginationDisabled"
            :link-gen="linkGen"
            :number-of-pages="Math.ceil(pagination.total / pagination.limit)"
            :use-router="true"
            limit="5"
            aria-controls="my-table"
            first-text="First"
            prev-text="Prev"
            next-text="Next"
            last-text="Last"
          />
        </div>
      </div>
    </div>
    <i-toast />
  </div>
</template>

<script>
import { iButton, iSpinner, iToast } from '@inscribe/signal';
import DocumentItem from '@/V2/documents/document-list/DocumentItem';
import * as documentStates from '@/constants/documentStates';
import Upload from '@/V2/documents/Upload';
import UploadButton from '@/V2/documents/UploadButton';
import CreateCollectSessionModal from '@/V2/collect/CreateCollectSessionModal';
import { mapGetters } from 'vuex';

export default {
  components: {
    CreateCollectSessionModal,
    'document-item': DocumentItem,
    upload: Upload,
    'upload-button': UploadButton,
    iButton,
    iSpinner,
    iToast,
  },
  props: {
    selectedCustomer: {
      type: Object,
      default: () => {},
    },
    showUploadButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showCollectModal: false,
    };
  },
  computed: {
    ...mapGetters({
      getDocumentsLoading: 'getDocumentsLoading',
      isUploadAccessBlocked: 'isUploadAccessBlocked',
      getCurrentFilterDocuments: 'getCurrentFilterDocuments',
      someDocumentUploading: 'someDocumentUploading',
    }),
    emptyListUnique() {
      return (
        this.$store.state.documents.length === 0 &&
        !this.$store.state.documentsLoading
      );
    },
    emptyList() {
      return this.emptyListUnique;
    },
    pagination() {
      const { pagination } = this.$store.state.documentsMetadata || {};
      return pagination;
    },
    hasPagination() {
      return this.pagination && this.pagination.number_of_pages > 1;
    },
    paginationDisabled() {
      return this.someDocumentUploading();
    },
    showCollectButton() {
      return this.$store.state.featureFlagsStore.featureFlags[
        'new-collect-session-button-enabled'
      ];
    },
  },
  methods: {
    linkGen(page) {
      return page === 1 ? '?' : `?page=${page}`;
    },
    fileAdded(file) {
      const document = {
        created_at: new Date().toISOString(),
        state: documentStates.UPLOADING,
        name: file.upload.filename,
        uploadProgress: 0,
        urls: {},
        uploadId: file.upload.uuid,
      };

      window.addEventListener(
        'beforeunload',
        this.preventRefreshWhileUploading,
      );
      this.$store.commit('addDocument', document);
    },
    updateFileProgress(file, uploadProgress) {
      const documents = [...this.$store.state.documents];
      const documentIndex = documents.findIndex(
        ({ uploadId }) => uploadId === file.upload.uuid,
      );

      documents[documentIndex] = {
        ...documents[documentIndex],
        uploadProgress,
      };

      this.$store.commit('setDocuments', documents);
    },
    uploadError(message, files) {
      const docList = this.$store.state.documents;
      const documents = docList && Array.isArray(docList) ? docList : [];
      const newDocuments = [...documents];
      files.forEach((file) => {
        const documentIndex = newDocuments.findIndex(
          ({ uploadId }) => uploadId === file.upload.uuid,
        );

        newDocuments[documentIndex] = {
          ...newDocuments[documentIndex],
          state: documentStates.ERROR_UPLOADING,
          error: message,
        };
      });

      this.$store.commit('setDocuments', newDocuments);
      this.maybeRemoveUnloadListener(newDocuments);
    },
    uploadFileSuccess(file, response) {
      const docList = this.$store.state.documents;
      const documents = docList && Array.isArray(docList) ? docList : [];
      const newDocuments = documents.map((document) => {
        if (document.uploadId === file.upload.uuid) {
          const newDocumentIndex = response.metadata.upload_ids.findIndex(
            (uploadId) => uploadId === file.upload.uuid,
          );
          if (newDocumentIndex > -1) {
            return response.data[newDocumentIndex];
          }
        }
        return document;
      });
      this.$store.commit('setDocuments', newDocuments);
      if (this.$store.state.documentsMetadata) {
        const { fraudResults, pagination } =
          this.$store.state.documentsMetadata;
        this.$store.commit('setDocumentsMetadata', {
          pagination: {
            ...pagination,
            total: pagination.total + 1,
          },
          fraudResults,
        });
      }

      this.maybeRemoveUnloadListener(newDocuments);
    },
    maybeRemoveUnloadListener(newDocuments) {
      const isUploading = newDocuments.some(
        ({ state }) => state === documentStates.UPLOADING,
      );
      if (!isUploading) {
        window.removeEventListener(
          'beforeunload',
          this.preventRefreshWhileUploading,
        );
      }
    },
    preventRefreshWhileUploading(event) {
      event.preventDefault();
      event.returnValue = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.document-list {
  align-items: center;

  &__header {
    align-items: center;
    margin: 16px 0;
    justify-content: space-between;

    .title {
      color: $light-neutral-120;
    }

    span {
      color: $light-neutral-110;
      padding-right: $font-size-secondary;
    }

    h6 {
      color: $color-navy-blue-60;
      font-size: $font-size-secondary;
      margin: 0;
      padding-right: $font-size-secondary;
    }
  }

  .i-spinner {
    margin-top: 100px;
  }

  .docs-list {
    border: 1px solid $light-neutral-60;
    border-radius: 16px;
  }

  .empty-state {
    min-height: 500px;
    .icon {
      font-size: $font-size-title - 4;
      background: $light-neutral-30;
      width: 120px;
      height: 120px;
      border-radius: 50%;
    }

    h5 {
      margin: 16px 0 0 0;
      color: $color-navy-blue;
      font-size: $font-size-default + 4;
      font-weight: 600;
    }
    p,
    .icon {
      color: $color-navy-blue-60;
    }
    p {
      font-size: $font-size-secondary;
    }
  }
  .dragging {
    min-height: 450px;
  }

  .pagination-wrapper {
    $menu-width-s: 64px;
    $menu-width-l: 160px;
    $detail-sidebar-width-l: 225px;

    box-shadow: $box-shadow-default;
    background-color: $color-white;
    width: calc(100% - #{$detail-sidebar-width-l} - #{$menu-width-l});
    left: $menu-width-l;
    position: fixed;
    bottom: 0;
    z-index: 0;

    @media screen and (max-width: 1100px) {
      left: $menu-width-s;
      width: calc(100% - #{$detail-sidebar-width-l} - #{$menu-width-s});
    }

    @media screen and (max-width: 768px) {
      left: 0;
      width: calc(100% - #{$detail-sidebar-width-l});
    }
  }
  .dz-message {
    position: static;
  }
}
</style>
